import React, { useLayoutEffect, useRef, useState } from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  evaluateMeasuredValue,
  normalizeIpqcRounds,
  rollupIpqcLot,
  toVnDateTimeLocal,
  type IpqcRound,
} from "@apv-erp/status";
import { normalizeReportStage } from "@/lib/schemas/qc";
import { displayCriterionName, isQuantitativeCriterion } from "./qc-form/CriteriaTable";
import type { IsoCriterionResult } from "./qc-form/checklist";
import { getDefectsForStage, type StageDefectOption } from "./StageDefectSelectorTable";

/**
 * Phiếu IPQC: thanh giai đoạn + bảng "Kiểm tra lặp lại" (mỗi lần một dòng, cột thông số lấy từ chỉ tiêu "lặp lại" của mẫu).
 * Giai đoạn lưu: SETUP / PATROL_IN_PROGRESS / FINAL_BATCH (mã cũ vẫn đọc qua normalizeReportStage).
 * Ký mẫu / Cho sản xuất không phải mã giai đoạn riêng mà là thời điểm ký / cho chạy trên phiếu.
 */

export type IpqcPatrolLogRow = IpqcRound;
export type IpqcPatrolLogEntry = IpqcRound;

export function normalizePatrolLogs(raw: any): IpqcPatrolLogRow[] {
  return normalizeIpqcRounds(raw);
}

/** Tổng chốt lô từ mẫu ký + các lần kiểm tra lặp lại */
export function calculateIpqcThreeStageRollup(setupSampleQty: number, setupDefectQty: number, patrolLogs: IpqcPatrolLogRow[] = []) {
  const t = rollupIpqcLot(setupSampleQty, setupDefectQty, patrolLogs);
  return {
    totalSampled: t.totalSampled,
    totalOk: t.totalOk,
    totalNg: t.totalNg,
    patrolCount: t.rounds,
    passRatePct: t.totalSampled > 0 ? Number(((t.totalOk / t.totalSampled) * 100).toFixed(1)) : 100,
  };
}

export const SAMPLE_FREQUENCY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "Mỗi 500 tờ / 15 phút", label: "Mỗi 500 tờ / 15 phút" },
  { value: "Mỗi 500 tờ / 30 phút", label: "Mỗi 500 tờ / 30 phút" },
  { value: "Mỗi 1.000 tờ", label: "Mỗi 1.000 tờ" },
  { value: "Mỗi 2.000 tờ", label: "Mỗi 2.000 tờ" },
  { value: "Mỗi 30 phút", label: "Mỗi 30 phút" },
  { value: "Mỗi 60 phút", label: "Mỗi 60 phút" },
  { value: "Mỗi pallet", label: "Mỗi pallet" },
];

/** Mã tần suất của form cũ → nhãn */
const LEGACY_FREQUENCY: Record<string, string> = {
  EVERY_30_MIN: "Mỗi 30 phút",
  EVERY_60_MIN: "Mỗi 60 phút",
  EVERY_500_SHEETS: "Mỗi 500 tờ",
  EVERY_1000_SHEETS: "Mỗi 1.000 tờ",
  EVERY_2000_SHEETS: "Mỗi 2.000 tờ",
  EVERY_PALLET: "Mỗi pallet",
};

export function frequencyLabel(v?: string | null): string {
  if (!v) return "";
  return LEGACY_FREQUENCY[v] || v;
}

/** Bước sản lượng giữa 2 lần kiểm từ tần suất ("Mỗi 500 tờ" → 500) */
function frequencyStep(v?: string | null): number {
  const label = frequencyLabel(v).replace(/\./g, "");
  const m = label.match(/(\d+)\s*(tờ|hộp|sp|cái)/i);
  return m ? Number(m[1]) : 1000;
}

// ───────────────────────── Thanh giai đoạn ─────────────────────────

interface StageBarProps {
  reportStage: string;
  onReportStageChange?: (stage: string) => void;
  /** Phiếu đợt 3: có bước Ký mẫu, các bước sau mở khi đã cho sản xuất */
  newFlow: boolean;
  setupDone?: boolean;
  signed?: boolean;
  released?: boolean;
  roundCount?: number;
}

export function IpqcStageBar({ reportStage, onReportStageChange, newFlow, setupDone, signed, released, roundCount = 0 }: StageBarProps) {
  const current = normalizeReportStage(reportStage);
  const steps: Array<{ key: string; label: string; done: boolean; clickable: boolean }> = newFlow
    ? [
        { key: "SETUP", label: "1. Duyệt setup", done: Boolean(setupDone), clickable: true },
        { key: "SIGNOFF", label: "2. Ký mẫu", done: Boolean(signed), clickable: false },
        { key: "PATROL_IN_PROGRESS", label: `3. Kiểm tra lặp lại (${roundCount})`, done: current === "FINAL_BATCH", clickable: Boolean(released) },
        { key: "FINAL_BATCH", label: "4. Chốt lô", done: false, clickable: Boolean(released) },
      ]
    : [
        { key: "SETUP", label: "1. Duyệt setup", done: false, clickable: true },
        { key: "PATROL_IN_PROGRESS", label: `2. Kiểm tra lặp lại (${roundCount})`, done: false, clickable: true },
        { key: "FINAL_BATCH", label: "3. Chốt lô", done: false, clickable: true },
      ];

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5" role="group" aria-label="Giai đoạn">
      {steps.map((st) => {
        const active = st.key === current;
        const cls = cn(
          "inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-xs font-bold transition-colors",
          active && "border-indigo-700 bg-indigo-600 text-white shadow-2xs",
          !active && st.done && "border-emerald-300 bg-emerald-50 text-emerald-800",
          !active && !st.done && "border-slate-200 bg-white text-slate-600",
        );
        if (!st.clickable || !onReportStageChange) {
          return (
            <span key={st.key} data-testid={`ipqc-stage-${st.key}`} data-done={st.done ? "true" : "false"} aria-current={active ? "step" : undefined} className={cls}>
              {st.done && !active ? <Check className="h-3 w-3" /> : null}
              {st.label}
            </span>
          );
        }
        return (
          <button
            key={st.key}
            type="button"
            aria-pressed={active}
            data-testid={`ipqc-stage-${st.key}`}
            data-done={st.done ? "true" : "false"}
            onClick={() => onReportStageChange(st.key)}
            className={cn(cls, "cursor-pointer", !active && "hover:bg-slate-50")}
          >
            {st.done && !active ? <Check className="h-3 w-3" /> : null}
            {st.label}
          </button>
        );
      })}
    </div>
  );
}

// ───────────────────────── Kiểm tra lặp lại ─────────────────────────

const RESULT_OPTIONS: Array<{ value: IpqcRound["result"]; label: string }> = [
  { value: "OK", label: "Đạt" },
  { value: "ADJUST", label: "Chỉnh máy" },
  { value: "HOLD", label: "Dừng máy" },
];

export const ROUND_RESULT_LABEL: Record<string, string> = { OK: "Đạt", ADJUST: "Chỉnh máy", HOLD: "Dừng máy" };

function cellEval(col: IsoCriterionResult, value: unknown): "PASS" | "FAIL" | null {
  if (value === undefined || value === null || value === "") return null;
  if (isQuantitativeCriterion(col)) return evaluateMeasuredValue(value, col.minValue, col.maxValue);
  const v = String(value).toUpperCase();
  return v === "PASS" || v === "FAIL" ? (v as "PASS" | "FAIL") : null;
}

export function roundCellText(col: IsoCriterionResult, value: unknown): string {
  if (value === undefined || value === null || value === "") return "—";
  if (isQuantitativeCriterion(col)) return String(value);
  const v = String(value).toUpperCase();
  return v === "PASS" ? "Đạt" : v === "FAIL" ? "Không đạt" : v === "NA" ? "N/A" : String(value);
}

export function roundColumnLabel(col: IsoCriterionResult): string {
  const name = displayCriterionName(col);
  const showUnit = col.unit && isQuantitativeCriterion(col) && col.unit !== name;
  return `${name}${showUnit ? ` (${col.unit})` : ""}`;
}

function useContainerWidth<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(1200);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
}

interface RoundsTableProps {
  rounds: IpqcRound[];
  onChange?: (rounds: IpqcRound[]) => void;
  /** Chỉ tiêu "lặp lại" của mẫu → cột đo */
  columns: IsoCriterionResult[];
  processStage?: string | null;
  sampleFrequency?: string | null;
  defaultSampleQty?: number;
  /** Chọn mã lỗi ở một lần kiểm → ghi vào bảng Lỗi phát hiện */
  onDefectPicked?: (opt: StageDefectOption, qty: number) => void;
  disabled?: boolean;
  disabledText?: string;
  /** Phiếu cũ: cột "Số đo / ghi chú" dạng chữ */
  legacy?: boolean;
}

export function IpqcRoundsTable({
  rounds,
  onChange,
  columns,
  processStage,
  sampleFrequency,
  defaultSampleQty = 5,
  onDefectPicked,
  disabled,
  disabledText,
  legacy,
}: RoundsTableProps) {
  const editable = Boolean(onChange) && !disabled;
  const stageDefects = getDefectsForStage(processStage);
  const { ref, width } = useContainerWidth<HTMLDivElement>();
  const compact = width < 720;

  const recompute = (row: IpqcRound, patch: Partial<IpqcRound>): IpqcRound => {
    const merged: IpqcRound = { ...row, ...patch };
    merged.sampleQty = Math.max(1, Math.round(Number(merged.sampleQty) || 1));
    merged.ngQty = Math.min(merged.sampleQty, Math.max(0, Math.round(Number(merged.ngQty) || 0)));
    merged.okQty = merged.sampleQty - merged.ngQty;
    if (patch.result === undefined && merged.result === "OK") {
      const anyFail = columns.some((c) => cellEval(c, merged.values?.[c.id]) === "FAIL");
      if (merged.ngQty > 0 || anyFail) merged.result = "ADJUST";
    }
    return merged;
  };

  const update = (idx: number, patch: Partial<IpqcRound>) => {
    if (!onChange) return;
    onChange(rounds.map((r, i) => (i === idx ? recompute(r, patch) : r)));
  };

  const setValue = (idx: number, colId: string, value: string | number | null) => {
    const row = rounds[idx];
    update(idx, { values: { ...(row.values || {}), [colId]: value } });
  };

  const addRound = () => {
    if (!onChange) return;
    const last = rounds[rounds.length - 1];
    const next: IpqcRound = {
      roundNo: rounds.length + 1,
      checkTime: toVnDateTimeLocal(new Date()).slice(11, 16),
      runningQty: (last?.runningQty || 0) + frequencyStep(sampleFrequency),
      sampleQty: Math.max(1, defaultSampleQty || 5),
      okQty: Math.max(1, defaultSampleQty || 5),
      ngQty: 0,
      defectCode: "",
      values: {},
      result: "OK",
      note: "",
    };
    onChange([...rounds, next]);
  };

  const removeRound = (idx: number) => {
    if (!onChange) return;
    onChange(rounds.filter((_, i) => i !== idx).map((r, i) => ({ ...r, roundNo: i + 1 })));
  };

  const pickDefect = (idx: number, code: string) => {
    update(idx, { defectCode: code });
    const opt = stageDefects.find((d) => d.code === code);
    if (opt && onDefectPicked) onDefectPicked(opt, Math.max(1, Number(rounds[idx]?.ngQty) || 1));
  };

  const numInput = (value: number, onValue: (v: number) => void, label: string, min = 0, cls = "", width = "") => (
    <Input
      type="number"
      min={min}
      aria-label={label}
      value={value ?? 0}
      readOnly={!editable}
      onChange={(e) => onValue(Math.max(min, Number(e.target.value) || 0))}
      className={cn("h-8 px-1.5 text-center text-xs font-mono font-bold", width, cls)}
    />
  );

  const valueCell = (row: IpqcRound, idx: number, col: IsoCriterionResult, large = false) => {
    const v = row.values?.[col.id];
    const ev = cellEval(col, v);
    if (!editable) {
      return <span className={cn("font-mono font-semibold", ev === "FAIL" && "text-red-700")}>{roundCellText(col, v)}</span>;
    }
    if (isQuantitativeCriterion(col)) {
      return (
        <Input
          type="number"
          step="any"
          inputMode="decimal"
          aria-label={`Lần ${row.roundNo} ${displayCriterionName(col)}`}
          value={v ?? ""}
          placeholder={col.targetValue !== undefined && col.targetValue !== null ? String(col.targetValue) : ""}
          onChange={(e) => setValue(idx, col.id, e.target.value === "" ? null : e.target.value)}
          className={cn(
            "px-1.5 text-center font-mono font-bold",
            large ? "h-10 text-sm" : "h-8 w-20 text-xs",
            ev === "FAIL" && "border-red-400 bg-red-50 text-red-800",
            ev === "PASS" && "border-emerald-300",
          )}
        />
      );
    }
    return (
      <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" role="group" aria-label={`Lần ${row.roundNo} ${displayCriterionName(col)}`}>
        {(["PASS", "FAIL"] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            aria-pressed={String(v).toUpperCase() === opt}
            onClick={() => setValue(idx, col.id, String(v).toUpperCase() === opt ? null : opt)}
            className={cn(
              "rounded px-2 py-1 text-[11px] font-bold",
              String(v).toUpperCase() === opt && opt === "PASS" && "bg-emerald-600 text-white",
              String(v).toUpperCase() === opt && opt === "FAIL" && "bg-red-600 text-white",
              String(v).toUpperCase() !== opt && "text-slate-600 hover:bg-slate-200/60",
            )}
          >
            {opt === "PASS" ? "Đạt" : "K.đạt"}
          </button>
        ))}
      </div>
    );
  };

  const resultSelect = (row: IpqcRound, idx: number) =>
    editable ? (
      <Select value={row.result || "OK"} onValueChange={(v) => update(idx, { result: v as IpqcRound["result"] })}>
        <SelectTrigger
          aria-label={`Lần ${row.roundNo} kết quả`}
          className={cn(
            "h-8 w-full min-w-24 text-[11px] font-bold",
            row.result === "HOLD" && "border-red-300 bg-red-50 text-red-700",
            row.result === "ADJUST" && "border-amber-300 bg-amber-50 text-amber-800",
            row.result === "OK" && "border-emerald-300 bg-emerald-50 text-emerald-700",
          )}
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {RESULT_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-xs">
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : (
      <span className={cn("font-bold", row.result === "HOLD" ? "text-red-700" : row.result === "ADJUST" ? "text-amber-800" : "text-emerald-700")}>
        {ROUND_RESULT_LABEL[row.result] || "Đạt"}
      </span>
    );

  const defectSelect = (row: IpqcRound, idx: number) =>
    editable ? (
      <Select value={row.defectCode || "NONE"} onValueChange={(v) => (v === "NONE" ? update(idx, { defectCode: "" }) : pickDefect(idx, v))}>
        <SelectTrigger aria-label={`Lần ${row.roundNo} mã lỗi`} className="h-8 w-full min-w-28 text-[11px]">
          <SelectValue placeholder="Không lỗi" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="NONE">Không lỗi</SelectItem>
          {stageDefects.map((d) => (
            <SelectItem key={d.code} value={d.code} className="text-xs">
              {d.code} · {d.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : (
      <span>{row.defectCode || "—"}</span>
    );

  const noteInput = (row: IpqcRound, idx: number, large = false) =>
    editable ? (
      <Input
        aria-label={`Lần ${row.roundNo} ghi chú`}
        value={(legacy ? row.measuredReading : row.note) || ""}
        onChange={(e) => update(idx, legacy ? { measuredReading: e.target.value } : { note: e.target.value })}
        placeholder={legacy ? "Số đo / ghi chú" : "Ghi chú"}
        className={large ? "h-9 text-sm" : "h-8 min-w-36 text-xs"}
      />
    ) : (
      <span className="text-slate-600">{[row.measuredReading, row.note].filter(Boolean).join(" · ") || "—"}</span>
    );

  return (
    <div ref={ref} className="min-w-0" data-testid="ipqc-rounds">
      {disabled && disabledText ? (
        <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-3 text-center text-xs text-slate-500">{disabledText}</div>
      ) : rounds.length === 0 ? (
        <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50/30 px-3 py-3 text-center text-xs text-slate-500">
          Chưa có lần kiểm. {editable ? "Bấm “+ Lần kiểm” khi đến tần suất." : ""}
        </div>
      ) : compact ? (
        <div className="space-y-2">
          {rounds.map((row, idx) => (
            <div key={idx} data-testid={`ipqc-round-${idx + 1}`} className="space-y-2 rounded-lg border border-slate-200 bg-white p-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-amber-800">Lần {row.roundNo}</span>
                <div className="flex items-center gap-1.5">
                  <div className="w-28">{resultSelect(row, idx)}</div>
                  {editable && (
                    <button type="button" aria-label={`Xoá lần ${row.roundNo}`} onClick={() => removeRound(idx)} className="rounded p-1.5 text-slate-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
                <label className="space-y-0.5">
                  <span>Giờ</span>
                  <Input
                    type="time"
                    aria-label={`Lần ${row.roundNo} giờ`}
                    value={row.checkTime || ""}
                    readOnly={!editable}
                    onChange={(e) => update(idx, { checkTime: e.target.value })}
                    className="h-9 text-center text-sm font-mono"
                  />
                </label>
                <label className="space-y-0.5">
                  <span>Mốc SL</span>
                  {numInput(row.runningQty, (v) => update(idx, { runningQty: v }), `Lần ${row.roundNo} mốc SL`)}
                </label>
                <label className="space-y-0.5">
                  <span>Mẫu</span>
                  {numInput(row.sampleQty, (v) => update(idx, { sampleQty: v }), `Lần ${row.roundNo} mẫu`, 1)}
                </label>
                <label className="space-y-0.5">
                  <span>Mẫu lỗi</span>
                  {numInput(row.ngQty, (v) => update(idx, { ngQty: v }), `Lần ${row.roundNo} mẫu lỗi`, 0, "border-rose-200 text-rose-800")}
                </label>
              </div>
              {columns.map((col) => (
                <div key={col.id} className="flex items-center justify-between gap-2 text-xs">
                  <span className="min-w-0 font-semibold text-slate-700">{roundColumnLabel(col)}</span>
                  <div className="w-32 shrink-0 text-right">{valueCell(row, idx, col, true)}</div>
                </div>
              ))}
              <div className="grid grid-cols-1 gap-2">
                {defectSelect(row, idx)}
                {noteInput(row, idx, true)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full min-w-max text-xs">
            <thead className="border-b border-slate-200 bg-slate-50 text-slate-600">
              <tr>
                <th className="w-12 px-2 py-2 text-center font-bold">Lần</th>
                <th className="w-24 px-1.5 py-2 text-center font-bold">Giờ</th>
                <th className="w-24 px-1.5 py-2 text-center font-bold">Mốc SL</th>
                <th className="w-16 px-1.5 py-2 text-center font-bold">Mẫu</th>
                <th className="w-16 px-1.5 py-2 text-center font-bold text-rose-700">Mẫu lỗi</th>
                <th className="w-36 px-1.5 py-2 text-left font-bold">Mã lỗi</th>
                {columns.map((col) => (
                  <th key={col.id} className="w-24 px-1.5 py-2 text-center font-bold" title={col.name}>
                    {roundColumnLabel(col)}
                  </th>
                ))}
                <th className="w-28 px-1.5 py-2 text-center font-bold">Kết quả</th>
                <th className="min-w-32 px-1.5 py-2 text-left font-bold">{legacy ? "Số đo / ghi chú" : "Ghi chú"}</th>
                {editable && <th className="w-9 px-1 py-2" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rounds.map((row, idx) => (
                <tr key={idx} data-testid={`ipqc-round-${idx + 1}`} className={row.result === "HOLD" ? "bg-red-50/40" : "hover:bg-amber-50/20"}>
                  <td className="px-2 py-1.5 text-center font-mono font-bold text-amber-800">{row.roundNo}</td>
                  <td className="px-1.5 py-1.5">
                    {editable ? (
                      <Input
                        type="time"
                        aria-label={`Lần ${row.roundNo} giờ`}
                        value={row.checkTime || ""}
                        onChange={(e) => update(idx, { checkTime: e.target.value })}
                        className="h-8 w-28 px-1 text-center text-xs font-mono"
                      />
                    ) : (
                      <span className="font-mono">{row.checkTime || "—"}</span>
                    )}
                  </td>
                  <td className="px-1.5 py-1.5">{numInput(row.runningQty, (v) => update(idx, { runningQty: v }), `Lần ${row.roundNo} mốc SL`, 0, "", "w-24")}</td>
                  <td className="px-1.5 py-1.5">{numInput(row.sampleQty, (v) => update(idx, { sampleQty: v }), `Lần ${row.roundNo} mẫu`, 1, "", "w-16")}</td>
                  <td className="px-1.5 py-1.5">
                    {numInput(row.ngQty, (v) => update(idx, { ngQty: v }), `Lần ${row.roundNo} mẫu lỗi`, 0, "border-rose-200 bg-rose-50/40 text-rose-800", "w-16")}
                  </td>
                  <td className="px-1.5 py-1.5">{defectSelect(row, idx)}</td>
                  {columns.map((col) => (
                    <td key={col.id} className="px-1.5 py-1.5 text-center">
                      {valueCell(row, idx, col)}
                    </td>
                  ))}
                  <td className="px-1.5 py-1.5 text-center">{resultSelect(row, idx)}</td>
                  <td className="px-1.5 py-1.5">{noteInput(row, idx)}</td>
                  {editable && (
                    <td className="px-1 py-1.5 text-center">
                      <button type="button" aria-label={`Xoá lần ${row.roundNo}`} onClick={() => removeRound(idx)} className="rounded p-1 text-slate-400 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editable && (
        <div className={cn("mt-2 flex", compact ? "sticky bottom-2 z-10 justify-stretch" : "justify-end")}>
          <Button
            type="button"
            size="sm"
            onClick={addRound}
            className={cn("h-8 bg-amber-600 text-xs font-bold text-white hover:bg-amber-700", compact && "h-10 w-full shadow-md")}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Lần kiểm
          </Button>
        </div>
      )}
    </div>
  );
}
